/** Read standard MIDI events and convert their times to microseconds. */
export interface MidiNote {
  startUs: number;
  durUs: number;
  channel: number;
  note: number;
  velocity: number;
}
export interface MidiSong {
  division: number;
  notes: MidiNote[];
  programs: Record<number, number>;
  durationUs: number;
}
const META_TEMPO = 0x51;
const META_END = 0x2f;
class Reader {
  pos = 0;
  constructor(private readonly data: Uint8Array) {}
  u8(): number {
    return this.data[this.pos++]!;
  }
  u16(): number {
    return (this.u8() << 8) | this.u8();
  }
  u32(): number {
    return ((this.u16() << 16) | this.u16()) >>> 0;
  }
  skip(n: number): void {
    this.pos += n;
  }
  eof(): boolean {
    return this.pos >= this.data.length;
  }
  chunk(): {
    id: string;
    len: number;
  } | null {
    if (this.eof()) return null;
    const id = String.fromCharCode(this.u8(), this.u8(), this.u8(), this.u8());
    const len = this.u32();
    return { id, len };
  }
  varlen(): number {
    let v = 0;
    for (;;) {
      const b = this.u8();
      v = (v << 7) | (b & 0x7f);
      if (!(b & 0x80)) return v;
    }
  }
}
export function parseMidi(data: Uint8Array): MidiSong {
  const r = new Reader(data);
  if (r.u32() !== 0x4d546864) throw new Error('not a MIDI file (MThd missing)');
  const headerLen = r.u32();
  r.u16(); // Consume the MIDI format field.
  const trackCount = r.u16();
  const division = r.u16();
  r.skip(headerLen - 6);
  const tempos: {
    tick: number;
    usPerQuarter: number;
  }[] = [{ tick: 0, usPerQuarter: 500000 }];
  const raw: {
    tick: number;
    type: string;
    channel: number;
    a: number;
    b: number;
  }[] = [];
  let endTick = 0;
  for (let t = 0; t < trackCount && !r.eof(); t++) {
    const chunk = r.chunk();
    if (!chunk || chunk.id !== 'MTrk') {
      if (chunk) r.skip(chunk.len);
      continue;
    }
    const end = r.pos + chunk.len;
    let tick = 0;
    let running = 0;
    while (r.pos < end) {
      tick += r.varlen();
      let status = r.u8();
      if (status < 0x80) {
        r.pos--;
        status = running;
      } else {
        running = status;
      }
      const hi = status & 0xf0;
      const channel = status & 0x0f;
      if (status === 0xff) {
        const meta = r.u8();
        const len = r.varlen();
        if (meta === META_TEMPO && len === 3) {
          const us = (r.u8() << 16) | (r.u8() << 8) | r.u8();
          tempos.push({ tick, usPerQuarter: us });
        } else {
          if (meta === META_END) endTick = Math.max(endTick, tick);
          r.skip(len);
        }
      } else if (status === 0xf0 || status === 0xf7) {
        r.skip(r.varlen());
      } else if (hi === 0x90 || hi === 0x80 || hi === 0xa0 || hi === 0xb0 || hi === 0xe0) {
        const a = r.u8();
        const b = r.u8();
        if (hi === 0x90) raw.push({ tick, type: (b & 0x7f) > 0 ? 'on' : 'off', channel, a, b });
        if (hi === 0x80) raw.push({ tick, type: 'off', channel, a, b });
      } else if (hi === 0xc0 || hi === 0xd0) {
        const a = r.u8();
        if (hi === 0xc0) raw.push({ tick, type: 'prog', channel, a, b: 0 });
      } else {
        break;
      }
    }
    r.pos = end;
  }
  tempos.sort((a, b) => a.tick - b.tick);
  const tickToUs = (tick: number): number => {
    let us = 0;
    for (let i = 0; i < tempos.length; i++) {
      const { tick: t0, usPerQuarter } = tempos[i]!;
      const t1 = i + 1 < tempos.length ? tempos[i + 1]!.tick : Infinity;
      if (tick <= t0) break;
      us += ((Math.min(tick, t1) - t0) * usPerQuarter) / division;
    }
    return Math.round(us);
  };
  const programs: Record<number, number> = {};
  for (const e of raw) {
    if (e.type === 'prog' && programs[e.channel] === undefined) programs[e.channel] = e.a;
  }
  const notes: MidiNote[] = [];
  const open = new Map<
    number,
    {
      tick: number;
      velocity: number;
      index: number;
    }
  >();
  for (const e of raw) {
    const key = e.channel * 128 + e.a;
    if (e.type === 'on') {
      open.set(key, { tick: e.tick, velocity: e.b, index: notes.length });
      notes.push({
        startUs: tickToUs(e.tick),
        durUs: 0,
        channel: e.channel,
        note: e.a,
        velocity: e.b,
      });
    } else if (e.type === 'off') {
      const on = open.get(key);
      if (on) {
        notes[on.index]!.durUs = Math.max(1, tickToUs(e.tick) - notes[on.index]!.startUs);
        open.delete(key);
      }
    }
  }
  for (const on of open.values()) notes[on.index]!.durUs = 500000;
  let durationUs = tickToUs(endTick);
  for (const n of notes) durationUs = Math.max(durationUs, n.startUs + n.durUs);
  return { division, notes, programs, durationUs };
}
